import React, { useEffect, useMemo, useRef, useState } from "react";
import CooperativeInfo from "./CooperativeInfo";
import Address from "./Address";
import Financials from "./Financials";
import Committee from "./Committee";
import ShareMember from "./ShareMember";
import Documents from "./Documents";
import Review from "./Review";
import Declaration from "./Declaration";
import { getRegisterFormInitialState } from "./registerFormState";
import {
  IAddress,
  ICommitteeMemberInput,
  ICooperativeDetails,
  IDeclaration,
  IDocumentsInput,
  IExecutiveMemberInput,
  IFinancialAuditInput,
  IFinancialIncome,
  IFinancialLoan,
  IFinancialShare,
  IFinancialTax,
  IShareMemberInput,
} from "../../exports/interfaces";
import { useLoader } from "../../context/LoaderContext";
import { dynamicSuccess, dynamicFailed } from "../../hooks/useToast";
import axiosClient from "../../axioxClient.ts/axiosClient";
import { useLocation, useNavigate } from "react-router-dom";
import useMobile from "../../hooks/useMobile";
import {
  ScanEye,
  ShieldCheck,
  FileText,
  User,
  UserRound,
  MapPin,
  Banknote,
  CircleCheck,
  List,
  ChevronRight,
} from "lucide-react";

interface RegisterEditProps {
  cooperative: ICooperativeDetails | null;
}

const RegisterEdit = ({ cooperative }: RegisterEditProps) => {
  const steps = [
    { label: "Cooperative Info", icon: List },
    { label: "Address", icon: MapPin },
    { label: "Financials", icon: Banknote },
    { label: "Committee & Executive", icon: UserRound },
    { label: "Share Member", icon: User },
    { label: "Documents", icon: FileText },
    { label: "Declaration", icon: ShieldCheck },
    { label: "Review", icon: ScanEye },
  ];

  const { setLoading } = useLoader();
  const navigate = useNavigate();
  const isMobile = useMobile();
  const location = useLocation();
  const editState = location.state as {
    step?: number;
    tab?: string;
  } | null;

  const initialState = useMemo(
    () => getRegisterFormInitialState(cooperative),
    [cooperative],
  );

  const [financialsactiveTab, setFinancialsActiveTab] = useState("share");
  const [committeeactiveTab, setCommitteeActiveTab] = useState("committees");
  const [currentStep, setCurrentStep] = useState(0);
  const stepperRef = useRef<HTMLDivElement>(null);

  const [cooperativeInfo, setCooperativeInfo] = useState<ICooperativeDetails>(
    initialState.cooperativeInfo,
  );
  const [address, setAddress] = useState<IAddress>(initialState.address);
  const [shareFinancial, setShareFinancial] = useState<IFinancialShare>(
    initialState.shareFinancial,
  );
  const [loanFinancial, setLoanFinancial] = useState<IFinancialLoan>(
    initialState.loanFinancial,
  );
  const [incomeFinancial, setIncomeFinancial] = useState<IFinancialIncome>(
    initialState.incomeFinancial,
  );
  const [auditFinancial, setAuditFinancial] = useState<IFinancialAuditInput>(
    initialState.auditFinancial,
  );
  const [taxFinancial, setTaxFinancial] = useState<IFinancialTax>(
    initialState.taxFinancial,
  );
  const [committeeCooperative, setCommitteeCooperative] =
    useState<ICommitteeMemberInput>(initialState.committeeCooperative);
  const [executiveCooperative, setExecutiveCooperative] =
    useState<IExecutiveMemberInput>(initialState.executiveCooperative);
  const [shareMember, setShareMember] = useState<IShareMemberInput>(
    initialState.shareMember,
  );
  const [documents, setDocuments] = useState<IDocumentsInput>(
    initialState.documents,
  );
  const [declaration, setDeclaration] = useState<IDeclaration>(
    initialState.declaration,
  );
  const [errors, setErrors] = useState<any>({});

  useEffect(() => {
    if (typeof editState?.step === "number") {
      setCurrentStep(editState.step);
    }

    if (editState?.tab) {
      if (["share", "loan", "income", "audit", "tax"].includes(editState.tab)) {
        setFinancialsActiveTab(editState.tab);
      }

      if (["committees", "executive"].includes(editState.tab)) {
        setCommitteeActiveTab(editState.tab);
      }
    }
  }, [editState]);

  useEffect(() => {
    if (cooperative) {
      const state = getRegisterFormInitialState(cooperative);
      setCooperativeInfo(state.cooperativeInfo);
      setAddress(state.address);
      setShareFinancial(state.shareFinancial);
      setLoanFinancial(state.loanFinancial);
      setIncomeFinancial(state.incomeFinancial);
      setAuditFinancial(state.auditFinancial);
      setTaxFinancial(state.taxFinancial);
      setCommitteeCooperative(state.committeeCooperative);
      setExecutiveCooperative(state.executiveCooperative);
      setShareMember(state.shareMember);
      setDocuments(state.documents);
      setDeclaration(state.declaration);
    }
  }, [cooperative]);

  const isValidEmail = (email: string) => /\S+@\S+\.\S+/.test(email);
  const isValidPhone = (phone: string) => /^\d{10}$/.test(phone);

  const validateContactFields = () => {
    const nextErrors: any = {};

    if (cooperativeInfo.email && !isValidEmail(cooperativeInfo.email)) {
      nextErrors.cooperativeInfo = {
        ...nextErrors.cooperativeInfo,
        email: "Invalid email format",
      };
    }

    if (
      cooperativeInfo.mobileNumber &&
      !isValidPhone(cooperativeInfo.mobileNumber)
    ) {
      nextErrors.cooperativeInfo = {
        ...nextErrors.cooperativeInfo,
        mobileNumber: "Mobile number must be 10 digits",
      };
    }

    if (
      committeeCooperative.email &&
      !isValidEmail(committeeCooperative.email)
    ) {
      setCommitteeActiveTab("committees");
      nextErrors.committeeCooperative = {
        ...nextErrors.committeeCooperative,
        email: "Invalid email format",
      };
    }

    if (
      executiveCooperative.email &&
      !isValidEmail(executiveCooperative.email)
    ) {
      setCommitteeActiveTab("executive");
      nextErrors.executiveCooperative = {
        ...nextErrors.executiveCooperative,
        email: "Invalid email format",
      };
    }

    if (
      executiveCooperative.mobile &&
      !isValidPhone(executiveCooperative.mobile)
    ) {
      setCommitteeActiveTab("executive");
      nextErrors.executiveCooperative = {
        ...nextErrors.executiveCooperative,
        mobile: "Mobile number must be 10 digits",
      };
    }

    if (
      auditFinancial.auditorMobile &&
      !isValidPhone(auditFinancial.auditorMobile)
    ) {
      setFinancialsActiveTab("audit");
      nextErrors.auditFinancial = {
        ...nextErrors.auditFinancial,
        auditorMobile: "Mobile number must be 10 digits",
      };
    }

    return nextErrors;
  };

  const validateStep = () => {
    const nextErrors = validateContactFields();
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const next = () => {
    if (currentStep < steps.length - 1) {
      if (validateStep()) {
        setCurrentStep((prev) => prev + 1);
        setFinancialsActiveTab("share");
        setCommitteeActiveTab("committees");
        scrollToStepper();
      }
    }
  };

  const prev = () => {
    if (currentStep > 0) {
      if (validateStep()) {
        setCurrentStep((prev) => prev - 1);
        setFinancialsActiveTab("share");
        setCommitteeActiveTab("committees");
        scrollToStepper();
      }
    }
  };

  const scrollToStepper = () => {
    setTimeout(() => {
      if (stepperRef.current) {
        stepperRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }, 200);
  };

  const ignoredKeys = [
    "file",
    "fileName",
    "documentUrl",
    "bodFile",
    "bodFileName",
    "bodDocumentUrl",
  ];

  const isObjectFilled = (obj: any) => {
    if (!obj) return false;

    return Object.entries(obj).every(([key, value]) => {
      if (
        ignoredKeys.includes(key) ||
        key.endsWith("File") ||
        key.endsWith("FileName") ||
        key.endsWith("DocumentUrl")
      ) {
        return true;
      }

      return value !== null && value !== "" && value !== undefined;
    });
  };

  const isFormComplete = () => {
    return (
      isObjectFilled(cooperativeInfo) &&
      isObjectFilled(address) &&
      isObjectFilled(shareFinancial) &&
      isObjectFilled(loanFinancial) &&
      isObjectFilled(incomeFinancial) &&
      isObjectFilled(auditFinancial) &&
      isObjectFilled(committeeCooperative) &&
      isObjectFilled(executiveCooperative) &&
      isObjectFilled(shareMember) &&
      isObjectFilled(documents)
    );
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("registrationNumber", cooperativeInfo.registrationNumber);
      formData.append(
        "businessregistrationNumber",
        cooperativeInfo.businessregistrationNumber,
      );
      formData.append("nameNepali", cooperativeInfo.nameNepali);
      formData.append("nameEnglish", cooperativeInfo.nameEnglish);
      formData.append("natureOfWork", cooperativeInfo.natureOfWork);
      formData.append("panNumber", cooperativeInfo.panNumber);
      formData.append("email", cooperativeInfo.email);
      formData.append("mobileNumber", cooperativeInfo.mobileNumber);
      formData.append("contactPerson", cooperativeInfo.contactPerson);
      formData.append("registeredDate", cooperativeInfo.registeredDate);
      formData.append(
        "registeredEnglishDate",
        cooperativeInfo.registeredEnglishDate,
      );
      formData.append("workingArea", cooperativeInfo.workingArea);
      formData.append("registeredAt", cooperativeInfo.registeredAt);
      formData.append(
        "registeredOfficeAddress",
        cooperativeInfo.registeredOfficeAddress,
      );
      formData.append(
        "isRegisteredInFederation",
        String(cooperativeInfo.isRegisteredInFederation),
      );
      formData.append("staffCount", String(cooperativeInfo.staffCount));
      formData.append("softwareUsed", cooperativeInfo.softwareUsed);
      formData.append("maleMembers", String(cooperativeInfo.maleMembers));
      formData.append("femaleMembers", String(cooperativeInfo.femaleMembers));
      formData.append("otherMembers", String(cooperativeInfo.otherMembers));

      formData.append("address[province]", address.province);
      formData.append("address[district]", address.district);
      formData.append("address[municipality]", address.municipality);
      formData.append("address[streetTole]", address.streetTole);
      formData.append("address[ward]", String(address.ward));

      Object.entries(shareFinancial).forEach(([key, value]) => {
        formData.append(`financial[share][${key}]`, String(value ?? ""));
      });
      Object.entries(loanFinancial).forEach(([key, value]) => {
        formData.append(`financial[loan][${key}]`, String(value ?? ""));
      });
      Object.entries(incomeFinancial).forEach(([key, value]) => {
        formData.append(`financial[income][${key}]`, String(value ?? ""));
      });
      Object.entries(auditFinancial).forEach(([key, value]) => {
        if (key !== "file" && key !== "fileName") {
          formData.append(`financial[audit][${key}]`, String(value ?? ""));
        }
      });
      if (auditFinancial.file) {
        formData.append("auditFile", auditFinancial.file);
      }
      Object.entries(taxFinancial).forEach(([key, value]) => {
        formData.append(`financial[tax][${key}]`, String(value ?? ""));
      });

      Object.entries(committeeCooperative).forEach(([key, value]) => {
        if (
          key !== "file" &&
          key !== "fileName" &&
          key !== "bodFile" &&
          key !== "bodFileName"
        ) {
          formData.append(
            `cooperativeandexecutive[committeemember][${key}]`,
            String(value ?? ""),
          );
        }
      });
      if (committeeCooperative.file) {
        formData.append("committeePhoto", committeeCooperative.file);
      }
      if (committeeCooperative.bodFile) {
        formData.append("bodDocument", committeeCooperative.bodFile);
      }

      Object.entries(executiveCooperative).forEach(([key, value]) => {
        if (key !== "file" && key !== "fileName") {
          formData.append(
            `cooperativeandexecutive[executivemember][${key}]`,
            String(value ?? ""),
          );
        }
      });
      if (executiveCooperative.file) {
        formData.append("executivePhoto", executiveCooperative.file);
      }

      Object.entries(shareMember).forEach(([key, value]) => {
        if (
          key !== "shareMemberFile" &&
          key !== "savingMemberFile" &&
          key !== "loanMemberFile"
        ) {
          formData.append(`shareMember[${key}]`, String(value ?? ""));
        }
      });
      if (shareMember.shareMemberFile)
        formData.append("shareMemberFile", shareMember.shareMemberFile);
      if (shareMember.savingMemberFile)
        formData.append("savingMemberFile", shareMember.savingMemberFile);
      if (shareMember.loanMemberFile)
        formData.append("loanMemberFile", shareMember.loanMemberFile);

      formData.append("declaration[name]", declaration.name);
      formData.append("declaration[position]", declaration.position);
      formData.append("declaration[mobile]", declaration.mobile);
      formData.append(
        "declaration[declaration]",
        String(declaration.declaration),
      );

      if (documents.regCertificateFile)
        formData.append("regCertificateUrl", documents.regCertificateFile);
      if (documents.lawDocumentFile)
        formData.append("lawDocumentUrl", documents.lawDocumentFile);
      if (documents.foundingDocumentFile)
        formData.append("foundingDocumentUrl", documents.foundingDocumentFile);
      if (documents.meetingDocumentFile)
        formData.append("meetingDocumentUrl", documents.meetingDocumentFile);
      if (documents.agmDocumentFile)
        formData.append("agmDocumentUrl", documents.agmDocumentFile);
      if (documents.otherDocumentFile)
        formData.append("otherDocumentUrl", documents.otherDocumentFile);
      if (documents.panDocumentFile)
        formData.append("panDocumentUrl", documents.panDocumentFile);
      if (documents.businessRegistrationFile)
        formData.append(
          "businessRegistrationUrl",
          documents.businessRegistrationFile,
        );
      if (documents.taxClearanceFile)
        formData.append("taxClearanceUrl", documents.taxClearanceFile);

      const response = await axiosClient.put(
        `/registers/${cooperative?.id}`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );

      if (response.status === 200 || response.status === 201) {
        dynamicSuccess("Updated successfully!");
        navigate("/cooperatives-list");
      }
    } catch (error: any) {
      dynamicFailed(`${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const progress = ((currentStep + 1) / steps.length) * 100;
  const disabled = !isFormComplete();

  return (
    <>
      <div
        className="d-flex justify-content-between align-items-center mb-3"
        ref={stepperRef}
      >
        <div>
          <h3>Cooperatives Registration Form</h3>
          <small>
            Please fill out all the required information in each step
          </small>
        </div>
      </div>
      <div className="row g-2">
        <div className="col-md-12">
          <div className="dashboard-card">
            <div className="card-body">
              <div className="stepper-container">
                <div className="stepper d-flex align-items-center justify-content-between flex-nowrap overflow-auto text-nowrap">
                  {steps.map((step, index) => {
                    const Icon = step.icon;
                    return (
                      <>
                        <div
                          key={index}
                          className={`step ${index <= currentStep ? "active" : ""}`}
                        >
                          {index < currentStep ? (
                            <i className="me-1">
                              <CircleCheck
                                size={20}
                                className="me-2 text-success"
                              />
                            </i>
                          ) : (
                            index === currentStep && (
                              <i className="me-1">
                                <Icon size={20} />
                              </i>
                            )
                          )}
                          <small
                            className={`step-label ${index === currentStep ? "active" : ""}`}
                          >
                            {step.label}
                          </small>
                        </div>
                        {index !== steps.length - 1 && (
                          <div
                            className={`step-arrow mx-1 ${index <= currentStep ? "active" : ""}`}
                          >
                            <i>
                              <ChevronRight />
                            </i>
                          </div>
                        )}

                        {index !== steps.length - 1 && (
                          <div
                            className={`step-line ${index < currentStep ? "active" : ""}`}
                          ></div>
                        )}
                      </>
                    );
                  })}
                </div>
                <div className="progress mt-3">
                  <div
                    className="progress-bar"
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="d-flex align-items-center my-3">
        {currentStep === 0 && (
          <>
            <div>
              <i className="primary-color me-2">
                <List size={30} />
              </i>
            </div>
            <div>
              <h5 className="m-0">Cooperative Info</h5>
              <small>
                Please fill out all the required information in each step
              </small>
            </div>
          </>
        )}
        {currentStep === 1 && (
          <>
            <div>
              <i className="primary-color me-2">
                <MapPin size={30} />
              </i>
            </div>
            <div>
              <h5 className="m-0">Cooperative Address</h5>
              <small>Please tell us your cooperative’s address details</small>
            </div>
          </>
        )}
        {currentStep === 2 && (
          <>
            <div>
              <i className="primary-color me-2">
                <Banknote size={30} />
              </i>
            </div>
            <div>
              <h5 className="m-0">Financial Info</h5>
              <small>Provide financial details of the cooperative</small>
            </div>
          </>
        )}
        {currentStep === 3 && (
          <>
            <div>
              <i className="primary-color me-2">
                <UserRound size={30} />
              </i>
            </div>
            <div>
              <h5 className="m-0">Committees & Executive</h5>
              <small>
                Enter the Committee and Executive details of the cooperative.
              </small>
            </div>
          </>
        )}
        {currentStep === 4 && (
          <>
            <div>
              <i className="primary-color me-2">
                <User size={30} />
              </i>
            </div>
            <div>
              <h5 className="m-0">Share member</h5>
              <small>Upload cooperative member.</small>
            </div>
          </>
        )}
        {currentStep === 5 && (
          <>
            <div>
              <i className="primary-color me-2">
                <FileText size={30} />
              </i>
            </div>
            <div>
              <h5 className="m-0">Documents</h5>
              <small>Upload the required documents for this section.</small>
            </div>
          </>
        )}
        {currentStep === 6 && (
          <>
            <div>
              <i className="primary-color me-2">
                <ShieldCheck size={30} />
              </i>
            </div>
            <div>
              <h5 className="m-0">Declaration</h5>
              <small>Details of the Person Submitting the Report</small>
            </div>
          </>
        )}
        {currentStep === 7 && (
          <>
            <div>
              <i className="primary-color me-2">
                <ScanEye size={30} />
              </i>
            </div>
            <div>
              <h5 className="m-0">Review All Info</h5>
              <small>Please review draft before submission.</small>
            </div>
          </>
        )}
      </div>

      {currentStep === 2 && (
        <ul className="nav nav-tabs mt-2 flex-nowrap overflow-auto text-nowrap">
          {[
            { key: "share", label: "Share" },
            { key: "loan", label: "Capital and Loan" },
            { key: "income", label: "Income and Expense" },
            { key: "audit", label: "Audit" },
            { key: "tax", label: "Tax" },
          ].map((tab) => (
            <li className="nav-item" key={tab.key}>
              <button
                className={`nav-link ${financialsactiveTab === tab.key ? "active" : ""}`}
                onClick={() => setFinancialsActiveTab(tab.key)}
              >
                {tab.label}
              </button>
            </li>
          ))}
        </ul>
      )}

      {currentStep === 3 && (
        <ul className="nav nav-tabs mt-2">
          {[
            { key: "committees", label: "Committees" },
            { key: "executive", label: "Executive" },
          ].map((tab) => (
            <li className="nav-item" key={tab.key}>
              <button
                className={`nav-link ${committeeactiveTab === tab.key ? "active" : ""}`}
                onClick={() => setCommitteeActiveTab(tab.key)}
              >
                {tab.label}
              </button>
            </li>
          ))}
        </ul>
      )}

      {currentStep === 0 && (
        <CooperativeInfo
          currentStep={currentStep}
          cooperativeInfo={cooperativeInfo}
          setCooperativeInfo={setCooperativeInfo}
          errors={errors.cooperativeInfo || {}}
        />
      )}
      {currentStep === 1 && (
        <Address
          currentStep={currentStep}
          address={address}
          setAddress={setAddress}
        />
      )}
      {currentStep === 2 && (
        <Financials
          currentStep={currentStep}
          financialsactiveTab={financialsactiveTab}
          shareFinancial={shareFinancial}
          setShareFinancial={setShareFinancial}
          loanFinancial={loanFinancial}
          setLoanFinancial={setLoanFinancial}
          incomeFinancial={incomeFinancial}
          setIncomeFinancial={setIncomeFinancial}
          auditFinancial={auditFinancial}
          setAuditFinancial={setAuditFinancial}
          taxFinancial={taxFinancial}
          setTaxFinancial={setTaxFinancial}
          errors={errors.auditFinancial || {}}
        />
      )}
      {currentStep === 3 && (
        <Committee
          committeeactiveTab={committeeactiveTab}
          committeeCooperative={committeeCooperative}
          setCommitteeCooperative={setCommitteeCooperative}
          executiveCooperative={executiveCooperative}
          setExecutiveCooperative={setExecutiveCooperative}
          errorsCommittee={errors.committeeCooperative || {}}
          errorsExecutive={errors.executiveCooperative || {}}
        />
      )}
      {currentStep === 4 && (
        <ShareMember
          shareMember={shareMember}
          setShareMember={setShareMember}
        />
      )}
      {currentStep === 5 && (
        <Documents documents={documents} setDocuments={setDocuments} />
      )}
      {currentStep === 6 && (
        <Declaration
          declaration={declaration}
          setDeclaration={setDeclaration}
        />
      )}
      {currentStep === 7 && (
        <Review
          cooperativeInfo={cooperativeInfo}
          address={address}
          shareFinancial={shareFinancial}
          loanFinancial={loanFinancial}
          incomeFinancial={incomeFinancial}
          auditFinancial={auditFinancial}
          committeeCooperative={committeeCooperative}
          executiveCooperative={executiveCooperative}
          shareMember={shareMember}
          documents={documents}
          declaration={declaration}
          setCurrentStep={setCurrentStep}
          scrollToStepper={() => {}}
          edit={true}
        />
      )}

      <div
        className={`d-flex mt-4 ${currentStep === 0 ? "justify-content-end" : "justify-content-between"} ${isMobile ? "flex-wrap gap-2" : ""}`}
      >
        {currentStep !== 0 && (
          <button className="btn px-3 btn-badge-border" onClick={prev}>
            Previous
          </button>
        )}
        <div className="d-flex gap-2">
          {currentStep === steps.length - 1 ? (
            <span
              title={disabled ? "Please fill all required fields" : ""}
              style={{ display: "inline-block" }}
            >
              <button
                className="btn btn-badge-primary px-3"
                onClick={handleSubmit}
                disabled={disabled}
                style={{ pointerEvents: disabled ? "none" : "auto" }}
              >
                Update
              </button>
            </span>
          ) : (
            <button className="btn btn-badge-primary px-3" onClick={next}>
              Next Step
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default RegisterEdit;
